import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TutorialProvider } from './contexts/TutorialContext';
import { CongregationProvider } from './contexts/CongregationContext';
import { UIPrefsProvider } from './contexts/UIPrefsContext';
import Layout from './components/layout/Layout';
import InstallPrompt from './components/layout/InstallPrompt';
import SplashScreen from './components/SplashScreen';
import ErrorBoundary from './components/ui/ErrorBoundary';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';

const DemoLogin = lazy(() => import('./pages/DemoLogin'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const MySchedule = lazy(() => import('./pages/MySchedule'));
const Teams = lazy(() => import('./pages/Teams'));
const Planning = lazy(() => import('./pages/Planning'));
const Availability = lazy(() => import('./pages/Availability'));
const SwapRequests = lazy(() => import('./pages/SwapRequests'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Settings = lazy(() => import('./pages/Settings'));
const Reports = lazy(() => import('./pages/Reports'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Leerhuis = lazy(() => import('./pages/Leerhuis'));
const OpenShifts = lazy(() => import('./pages/OpenShifts'));
const CoordinatorDashboard = lazy(() => import('./pages/CoordinatorDashboard'));
const Announcements = lazy(() => import('./pages/Announcements'));
const Rooms = lazy(() => import('./pages/Rooms'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const NotFoundLazy = lazy(() => import('./pages/NotFound'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 dark:border-slate-600 dark:border-t-slate-300 rounded-full animate-spin" />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <SplashScreen />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children, requiredRoles }: { children: React.ReactNode; requiredRoles: string[] }) {
  const { profile, loading } = useAuth();

  if (loading) return <SplashScreen />;

  if (!profile || !requiredRoles.includes(profile.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <SplashScreen />;

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/demo/:role"
        element={
          <Suspense fallback={<SplashScreen />}>
            <DemoLogin />
          </Suspense>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <Suspense fallback={<SplashScreen />}>
              <ForgotPassword />
            </Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<SplashScreen />}>
            <ResetPassword />
          </Suspense>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="mijn-rooster" element={<Lazy><MySchedule /></Lazy>} />
        <Route path="beschikbaarheid" element={<Lazy><Availability /></Lazy>} />
        <Route path="ruilverzoeken" element={<Lazy><SwapRequests /></Lazy>} />
        <Route path="meldingen" element={<Lazy><Notifications /></Lazy>} />
        <Route path="teams" element={<AdminRoute requiredRoles={['superadmin', 'beheerder', 'groepsbeheerder']}><Lazy><Teams /></Lazy></AdminRoute>} />
        <Route path="planning" element={<Lazy><Planning /></Lazy>} />
        <Route path="leerhuis" element={<Lazy><Leerhuis /></Lazy>} />
        <Route path="vrije-diensten" element={<Lazy><OpenShifts /></Lazy>} />
        <Route path="berichten" element={<Lazy><Announcements /></Lazy>} />
        <Route path="zalen" element={<AdminRoute requiredRoles={['superadmin', 'beheerder', 'groepsbeheerder']}><Lazy><Rooms /></Lazy></AdminRoute>} />
        <Route path="rapportages" element={<AdminRoute requiredRoles={['superadmin', 'beheerder', 'groepsbeheerder']}><Lazy><Reports /></Lazy></AdminRoute>} />
        <Route path="coordinator" element={<AdminRoute requiredRoles={['superadmin', 'beheerder', 'groepsbeheerder']}><Lazy><CoordinatorDashboard /></Lazy></AdminRoute>} />
        <Route path="gebruikers" element={<AdminRoute requiredRoles={['superadmin', 'beheerder']}><Lazy><UserManagement /></Lazy></AdminRoute>} />
        <Route path="instellingen" element={<Lazy><Settings /></Lazy>} />
        <Route path="auditlog" element={<AdminRoute requiredRoles={['superadmin', 'beheerder']}><Lazy><AuditLog /></Lazy></AdminRoute>} />
        <Route path="*" element={<Lazy><NotFoundLazy /></Lazy>} />
      </Route>
      <Route path="*" element={<ErrorBoundary><NotFound /></ErrorBoundary>} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <CongregationProvider>
            <AuthProvider>
              <ToastProvider>
                <UIPrefsProvider>
                  <TutorialProvider>
                    <AppRoutes />
                    <InstallPrompt />
                  </TutorialProvider>
                </UIPrefsProvider>
              </ToastProvider>
            </AuthProvider>
          </CongregationProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}